/* global google */
// Complete ApiService with fixed LLM payload format

import { getEnv } from "../config/env";
import {
	DEFAULT_DOMAIN_EXCLUDE_QUERY_THRESHOLD,
	DEFAULT_DOMAIN_INCLUDE_QUERY_THRESHOLD,
} from "../constants";

class LLMApiService {
	constructor() {
		// Update these URLs to your actual endpoints
		const env = getEnv();
		this.llmApiUrl = `${env.REACT_APP_PROXY}/domains/search-text`;
		this.domainApiUrl = `${env.REACT_APP_PROXY}/domains/search`;
		this.timeout = 300000;
		this.tokenChangeListeners = [];

		this.googleClientId = env.REACT_APP_GOOGLE_CLIENT_ID;
		this.googleScopes = env.REACT_APP_GOOGLE_SCOPES || "openid email profile";

		this.TOKEN_STORAGE_KEY = "google_id_token";
		this.tokens = {
			idToken: null,
			accessToken: null,
			csrfToken: null,
			expiresAt: null,
		};

		this.tokenClient = null;
		this.idTokenClient = null;
		this.isInitializing = false;
		this.isInitialized = false;

		console.log("📡 ApiService initializing...");

		this.loadStoredToken();
		this.initializeGoogleIdentityServices();
	}

	// ==================== TOKEN MANAGEMENT ====================

	onTokenChange(callback) {
		this.tokenChangeListeners.push(callback);
		return () => {
			this.tokenChangeListeners = this.tokenChangeListeners.filter(
				(cb) => cb !== callback,
			);
		};
	}

	notifyTokenChange(event) {
		this.tokenChangeListeners.forEach((callback) => {
			try {
				callback(event, {
					isAuthenticated: this.isAuthenticated(),
					user: this.getUser(),
				});
			} catch (error) {
				console.error("❌ Token change listener error:", error);
			}
		});
	}

	storeToken(token) {
		if (!token || !this.isValidJWT(token)) {
			console.error("❌ Invalid token provided");
			return false;
		}

		try {
			this.tokens.idToken = token;
			const payload = this.parseJWT(token);
			if (payload?.exp) {
				this.tokens.expiresAt = payload.exp * 1000;
			}

			localStorage.setItem(this.TOKEN_STORAGE_KEY, token);
			console.log("✅ Token stored successfully");
			this.notifyTokenChange("stored");
			return true;
		} catch (error) {
			console.error("❌ Token storage failed:", error.message);
			return false;
		}
	}

	loadStoredToken() {
		try {
			let token = localStorage.getItem(this.TOKEN_STORAGE_KEY);
			if (!token) {
				token = sessionStorage.getItem(this.TOKEN_STORAGE_KEY);
			}

			if (token && this.isValidJWT(token)) {
				this.tokens.idToken = token;
				const payload = this.parseJWT(token);
				if (payload?.exp) {
					this.tokens.expiresAt = payload.exp * 1000;
				}

				if (this.isIdTokenValid()) {
					console.log("✅ Valid token loaded for:", payload?.email);
				}
			}
		} catch (error) {
			console.error("❌ Error loading token:", error.message);
			this.clearStoredToken();
		}
	}

	clearStoredToken() {
		try {
			localStorage.removeItem(this.TOKEN_STORAGE_KEY);
			sessionStorage.removeItem(this.TOKEN_STORAGE_KEY);
		} catch (error) {
			console.warn("⚠️ Error clearing storage:", error.message);
		}

		this.tokens.idToken = null;
		this.tokens.accessToken = null;
		this.tokens.expiresAt = null;
		this.notifyTokenChange("cleared");
		console.log("🧹 Tokens cleared");
	}

	isValidJWT(token) {
		return token && typeof token === "string" && token.split(".").length === 3;
	}

	isIdTokenValid() {
		if (!this.tokens.idToken) return false;
		try {
			const payload = this.parseJWT(this.tokens.idToken);
			if (!payload?.exp) return false;
			return payload.exp * 1000 > Date.now();
		} catch {
			return false;
		}
	}

	isAuthenticated() {
		return this.isIdTokenValid();
	}

	parseJWT(token) {
		try {
			const base64Url = token.split(".")[1];
			const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
			const jsonPayload = decodeURIComponent(
				Array.from(atob(base64))
					.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
					.join(""),
			);
			return JSON.parse(jsonPayload);
		} catch {
			return null;
		}
	}

	getUser() {
		if (this.tokens.idToken && this.isIdTokenValid()) {
			const payload = this.parseJWT(this.tokens.idToken);
			return payload
				? {
						id: payload.sub,
						email: payload.email,
						name: payload.name,
						picture: payload.picture,
						email_verified: payload.email_verified,
					}
				: null;
		}
		return null;
	}

	setProxyUrl(url) {
		const base = url.replace(/\/$/, "");
		this.llmApiUrl = `${base}/domains/search-text`;
		this.domainApiUrl = `${base}/domains/search`;
	}

	// ==================== GOOGLE AUTHENTICATION ====================

	async initializeGoogleIdentityServices() {
		if (this.isInitializing || this.isInitialized) return;
		this.isInitializing = true;

		try {
			await this.waitForGoogleIdentityServices();

			if (!this.googleClientId) {
				console.warn(
					"⚠️ Google Client ID not configured - Google OAuth will not be available",
				);
				console.log("💡 To enable Google OAuth:");
				console.log("   1. Create a Google Cloud Project");
				console.log("   2. Enable Google+ API");
				console.log("   3. Create OAuth 2.0 credentials");
				console.log("   4. Add REACT_APP_GOOGLE_CLIENT_ID to your .env file");
				console.log("   5. Restart your development server");
				this.isInitializing = false;
				return;
			}

			google.accounts.id.initialize({
				client_id: this.googleClientId,
				callback: this.handleIdTokenResponse.bind(this),
				auto_select: false,
				cancel_on_tap_outside: true,
				itp_support: true,
				ux_mode: "popup",
			});

			this.generateCSRFToken();
			this.isInitialized = true;
			console.log(
				"✅ Google Identity Services initialized with client ID:",
				this.googleClientId,
			);
		} catch (error) {
			console.error("❌ Failed to initialize Google Identity Services:", error);
		} finally {
			this.isInitializing = false;
		}
	}

	async waitForGoogleIdentityServices() {
		return new Promise((resolve, reject) => {
			let attempts = 0;
			const maxAttempts = 50;

			const checkGoogle = () => {
				attempts++;
				if (typeof window !== "undefined" && window.google?.accounts) {
					resolve();
				} else if (attempts >= maxAttempts) {
					reject(new Error("Google Identity Services failed to load"));
				} else {
					setTimeout(checkGoogle, 100);
				}
			};
			checkGoogle();
		});
	}

	async signIn() {
		try {
			console.log("🔐 Starting sign-in...");
			if (!this.isInitialized) {
				await this.initializeGoogleIdentityServices();
			}

			const idTokenResponse = await this.requestIdToken();
			if (idTokenResponse && this.tokens.idToken) {
				this.storeToken(this.tokens.idToken);
				return {
					success: true,
					method: "id_token",
					token: this.tokens.idToken,
					user: this.getUser(),
				};
			}

			throw new Error("ID token request failed");
		} catch (error) {
			console.error("❌ Sign-in failed:", error);
			return {
				success: false,
				error: error.message,
			};
		}
	}

	async requestIdToken() {
		return new Promise((resolve, reject) => {
			this.idTokenPromise = { resolve, reject };
			google.accounts.id.prompt((notification) => {
				if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
					reject(new Error("One Tap not available"));
				}
			});
		});
	}

	handleIdTokenResponse(response) {
		try {
			if (response.error) {
				console.error("❌ ID token error:", response.error);
				this.idTokenPromise?.reject(new Error(response.error));
				return;
			}

			const idToken = response.credential;
			if (!idToken) {
				this.idTokenPromise?.reject(new Error("No credential received"));
				return;
			}

			const success = this.storeToken(idToken);
			if (success) {
				const user = this.getUser();
				console.log("✅ Authentication successful for:", user?.email);
				this.idTokenPromise?.resolve({
					success: true,
					credential: idToken,
					user: user,
				});
			} else {
				this.idTokenPromise?.reject(new Error("Failed to store token"));
			}
		} catch (error) {
			console.error("❌ Error handling ID token:", error);
			this.idTokenPromise?.reject(error);
		} finally {
			this.idTokenPromise = null;
		}
	}

	signOut() {
		this.clearStoredToken();
		if (
			typeof google !== "undefined" &&
			google.accounts?.id?.disableAutoSelect
		) {
			google.accounts.id.disableAutoSelect();
		}
		console.log("✅ Signed out");
		return { success: true };
	}

	reversePayloadToFilters(llmResponse) {
		console.log(
			"🔄 Reversing LLM response to FiltersSidebar format:",
			llmResponse,
		);

		if (!llmResponse || !llmResponse.filters) {
			console.warn("⚠️ Invalid LLM response structure");
			return null;
		}

		const { filters: llmFilters } = llmResponse;

		// ✅ HELPER FUNCTIONS for reverse conversions
		const convertDecimalToPercentage = (decimal) => {
			if (!decimal) return 0;
			return Math.round(decimal * 100);
		};

		const convertNumberToReach = (number) => {
			if (!number) return 0;
			if (number >= 1000000) return `${(number / 1000000).toFixed(1)}M`;
			if (number >= 1000) return `${(number / 1000).toFixed(1)}K`;
			return number;
		};

		// ✅ ADD: Process query and excludeQuery from LLM response
		const processSearchQueries = (llmResponse) => {
			const searchTerms = [];

			// Process include queries
			if (llmResponse.query && Array.isArray(llmResponse.query)) {
				llmResponse.query.forEach((queryItem, index) => {
					searchTerms.push({
						id: Date.now() + index,
						text: queryItem.query,
						score: 65,
						type: "include",
					});
				});
			}

			// Process exclude queries
			if (llmResponse.excludeQuery && Array.isArray(llmResponse.excludeQuery)) {
				llmResponse.excludeQuery.forEach((queryItem, index) => {
					searchTerms.push({
						id: Date.now() + 1000 + index, // Offset to avoid ID conflicts
						text: queryItem.query,
						score: 58,
						type: "exclude",
					});
				});
			}

			return searchTerms;
		};

		const searchTerms = processSearchQueries(llmResponse);

		// ✅ REVERSE CUSTOM FILTERS from performanceData array
		const reverseCustomFilters = (perfDataArray) => {
			// Map the performance data from LLM (if any exists)
			const mappedFilters =
				!perfDataArray ||
				!Array.isArray(perfDataArray) ||
				perfDataArray.length === 0
					? []
					: perfDataArray.map((p, idx) => ({
							id: Date.now() + idx + 100,
							column: reverseMapCustomFilterColumn(p.filterColumn || p.column),
							threshold: p.filterThreshold || p.threshold || 0,
						}));

			// Check which default columns are missing from the mapped filters
			const existingColumns = new Set(mappedFilters.map((f) => f.column));

			// Add defaults only if those specific columns don't exist
			const defaultsToAdd = [];

			if (!existingColumns.has("cprmarketrequests")) {
				defaultsToAdd.push({
					id: Date.now(),
					column: "cprmarketrequests",
					threshold: llmFilters.performance?.minMarketRequests || 10000,
				});
			}

			if (!existingColumns.has("impressions")) {
				defaultsToAdd.push({
					id: Date.now() + 1,
					column: "impressions",
					threshold: llmFilters.performance?.minImpressions || 100,
				});
			}

			// Combine mapped filters with only the missing defaults
			return [...mappedFilters, ...defaultsToAdd];
		};

		// ✅ REVERSE COLUMN MAPPING
		const reverseMapCustomFilterColumn = (apiColumn) => {
			const columnMap = {
				cprmarketrequests: "cprmarketrequests",
				totalImpressions: "impressions",
				totalViewables: "viewables",
				totalViewabilityMeasures: "viewabilitymeasures",
				totalCompleteFirstOccurrence: "completefirstoccurrence",
				totalStartFirstOccurrence: "startfirstoccurrence",
				totalCprClicks: "cprclicks",
				totalFills: "fills",
				totalNonZeroBids: "nonzerobids",
				totalDealVolumeDealInfo: "dealvolumedealinfo",
				totalNonZeroBidsValueInUsd: "nonzerobidsvalueinusd",
				totalBidSpendInUsd: "bidspendinusd",
				calculated_clickthroughrate: "clickthroughrate",
				calculated_revenueperimpression: "revenueperimpression",
				calculated_costperimpression: "costperimpression",
				calculated_bid_guidance: "bid_guidance",
				totalImpressionsWithClickMacro: "impressionswithclickmacro",
				totalCprMarketDemandPartnerSpendInUsd:
					"cprmarketdemandpartnerspendinusd",
				totalCprMarketPublisherRevenueInUsd: "cprmarketpublisherrevenueinusd",
				totalCprAllRequests: "cprallrequests",
				totalCprPrivateMarketImpressions: "cprprivatemarketimpressions",
			};

			return columnMap[apiColumn] || apiColumn;
		};

		// ✅ REVERSE GEOGRAPHY MAPPING
		const reverseGeographyMapping = (geographyArray) => {
			if (!geographyArray || !Array.isArray(geographyArray)) {
				return [];
			}
			return geographyArray;
		};

		// ✅ BUILD REVERSED FILTERS OBJECT
		const reversedFilters = {
			// ✅ Media and Device Filters
			searchTerms: searchTerms,
			mediaSubtype: llmFilters.domainFilters?.mediaSubtype || ["Display"],
			deviceTypes: llmFilters.domainFilters?.deviceTypes || [],
			videoPlacement: llmFilters.domainFilters?.videoplcmt || [],
			distributionChannel: llmFilters.domainFilters?.distributionChannel || [
				"Site",
			],

			// ✅ Geography (convert from country codes to selectedCountries)
			selectedCountries: reverseGeographyMapping(
				llmFilters.domainFilters?.geography,
			),
			geography: llmFilters.domainFilters?.geography?.[0] || "US", // Primary country

			// ✅ Content and Safety
			contentStyle:
				llmFilters.domainFilters?.contentStyle ||
				llmFilters.vectorFilters?.content_style ||
				[],
			safetyTier: llmFilters.vectorFilters?.safetyTier || [],
			//categories: llmFilters.vectorFilters?.iab_category || [],

			// ✅ Performance Metrics (convert from decimal to percentage/display format)
			minViewability: convertDecimalToPercentage(
				llmFilters.performance?.minViewability,
			),
			minReach: llmFilters.performance?.minReach || 0,

			// ✅ Publishers
			publishers: Array.isArray(llmFilters.domainFilters?.publisher)
				? llmFilters.domainFilters.publisher
				: [],
			isDirect: llmFilters.domainFilters?.isDirect?.toString() === "true",

			// ✅ Custom Performance Filters
			customFilters: reverseCustomFilters(
				llmFilters.performance?.performanceData,
			),

			// ✅ Other Settings
			limit: llmFilters.noOfResult || 1000,
			dateRange: llmFilters.noOfDays || 30,

			// ✅ Uploaded Domains
			uploadedDomains: {
				includeList: llmFilters.includeDomains || [],
				excludeList: llmFilters.excludeDomains || [],
			},

			// ✅ Relevance/Threshold Settings
			relevance: Math.round(
				(llmFilters.vectorFilters?.categoryThreshold?.iab_category_score ||
					0.6) * 100,
			),
			relevanceThreshold: Math.round(
				(llmFilters.vectorFilters?.categoryThreshold?.iab_category_score ||
					0.6) * 100,
			),

			// ✅ Default values for fields not in LLM response
			language: ["English"],
			minCTR: 0,
			minReachThreshold: 0,
			totalReach: "25M+",
			minViewabilityThreshold: 0,
			minCTRVTR: 0,

			// ✅ Individual content style fields (for compatibility)
			content_style_1: [],
			content_style_2: [],
			content_style_3: [],
			safety_tier_1: [],
			safety_tier_2: [],
			safety_tier_3: [],
		};

		console.log("✅ Reversed filters for FiltersSidebar:", reversedFilters);
		return reversedFilters;
	}

	// ✅ HELPER METHOD: Reverse column mapping
	reverseMapCustomFilterColumn(apiColumn) {
		const columnMap = {
			cprmarketrequests: "cprmarketrequests",
			totalImpressions: "impressions",
			totalViewables: "viewables",
			totalViewabilityMeasures: "viewabilitymeasures",
			totalCompleteFirstOccurrence: "completefirstoccurrence",
			totalStartFirstOccurrence: "startfirstoccurrence",
			totalCprClicks: "cprclicks",
			totalFills: "fills",
			totalNonZeroBids: "nonzerobids",
			totalDealVolumeDealInfo: "dealvolumedealinfo",
			totalNonZeroBidsValueInUsd: "nonzerobidsvalueinusd",
			totalBidSpendInUsd: "bidspendinusd",
			calculated_clickthroughrate: "clickthroughrate",
			calculated_revenueperimpression: "revenueperimpression",
			calculated_costperimpression: "costperimpression",
			calculated_bid_guidance: "bid_guidance",
			totalImpressionsWithClickMacro: "impressionswithclickmacro",
			totalCprMarketDemandPartnerSpendInUsd: "cprmarketdemandpartnerspendinusd",
			totalCprMarketPublisherRevenueInUsd: "cprmarketpublisherrevenueinusd",
			totalCprAllRequests: "cprallrequests",
			totalCprPrivateMarketImpressions: "cprprivatemarketimpressions",
		};

		return columnMap[apiColumn] || apiColumn;
	}

	// ==================== UPDATE: EXISTING performDomainSearch METHOD =============

	async performDomainSearch(llmResponsePayload) {
		try {
			console.log(
				"🔍 Performing domain search with LLM response as payload...",
			);

			const enhancedPayload = {
				...llmResponsePayload,
				...(llmResponsePayload.query && {
					query: llmResponsePayload.query.map((term) => ({
						...term,
						threshold: DEFAULT_DOMAIN_INCLUDE_QUERY_THRESHOLD,
					})),
				}),
				...(llmResponsePayload.excludeQuery && {
					excludeQuery: llmResponsePayload.excludeQuery.map((term) => ({
						...term,
						threshold: DEFAULT_DOMAIN_EXCLUDE_QUERY_THRESHOLD,
					})),
				}),
				filters: {
					...llmResponsePayload.filters,
					performance: {
						...llmResponsePayload.filters?.performance,
						performanceData: (() => {
							const existingData =
								llmResponsePayload.filters?.performance?.performanceData || [];

							// Check which default columns are missing
							const existingColumns = new Set(
								existingData.map((item) => item.filterColumn || item.column),
							);

							// Add defaults only if those specific columns don't exist
							const defaultsToAdd = [];

							if (!existingColumns.has("cprmarketrequests")) {
								defaultsToAdd.push({
									filterColumn: "cprmarketrequests",
									filterThreshold: 10000,
								});
							}

							if (
								!existingColumns.has("impressions") &&
								!existingColumns.has("totalImpressions")
							) {
								defaultsToAdd.push({
									filterColumn: "impressions",
									filterThreshold: 100,
								});
							}
							return [...existingData, ...defaultsToAdd];
						})(),
					},
					vectorFilters: {
						...llmResponsePayload.filters?.vectorFilters,
						iab_category: [],
					},
					cumulativeDataFilters: {
						perfomanceData: "avg_viewability",
						reachThreashold: 0,
						marketThreashold: 700000000000,
					},
				},
			};

			const response = await this.makeAuthenticatedRequest(this.domainApiUrl, {
				method: "POST",
				body: JSON.stringify(enhancedPayload),
			});

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`Domain API error: ${response.status} - ${errorText}`);
			}

			const data = await response.json();
			console.log("✅ Domain search response received:", data);

			// ✅ FIXED: Use this.reversePayloadToFilters (not reversePayloadToFilters)
			const reversedFilters = this.reversePayloadToFilters(llmResponsePayload);

			return {
				success: true,
				data: {
					domains: data?.body?.domain || data?.domain || [],
					keywords: data?.body?.keywords || data?.keywords || [],
					totalRecords: data?.body?.totalRecords || data?.totalRecords || 0,
					cumulativeData:
						data?.body?.cumulativeData || data?.cumulativeData || [],
					body: {
						domain: data?.body?.domain || data?.domain || [],
						keywords: data?.body?.keywords || data?.keywords || [],
						totalRecords: data?.body?.totalRecords || data?.totalRecords || 0,
						cumulativeData:
							data?.body?.cumulativeData || data?.cumulativeData || [],
					},

					// ✅ ADD: Reversed filters for UI population
					appliedFilters: reversedFilters,
					originalLLMPayload: llmResponsePayload,

					success: true,
				},
				error: null,
			};
		} catch (error) {
			console.error("❌ Domain search error:", error);
			return {
				success: false,
				data: null,
				error: error.message,
			};
		}
	}

	// ==================== API METHODS ====================

	getHeaders() {
		const headers = {
			Accept: "application/json",
			"Content-Type": "application/json",
		};

		if (this.tokens?.idToken && this.isIdTokenValid()) {
			headers["Authorization"] = `Bearer ${this.tokens.idToken}`;
		}

		return headers;
	}

	async makeAuthenticatedRequest(url, options = {}) {
		try {
			if (!this.isAuthenticated()) {
				const signInResult = await this.signIn();
				if (!signInResult.success) {
					throw new Error("Authentication required");
				}
			}

			const headers = this.getHeaders();
			const requestOptions = {
				method: "GET",
				...options,
				headers: { ...headers, ...options.headers },
				mode: "cors",
				credentials: "omit",
				cache: "no-cache",
			};

			const response = await fetch(url, {
				...requestOptions,
				signal: AbortSignal.timeout(this.timeout),
			});

			return response;
		} catch (error) {
			console.error("❌ API request failed:", error);
			throw error;
		}
	}

	// ==================== FIXED LLM API METHOD ====================

	async searchWithLLM(searchQuery, searchType = "domain") {
		try {
			console.log("🤖 Calling LLM API for:", searchQuery, "Type:", searchType);

			// ✅ FIXED: Use the correct payload format expected by your API
			const payload = {
				inputText: searchQuery, // ✅ Changed from 'query' to 'inputText'
				searchType: searchType.toUpperCase(), // ✅ Convert to uppercase (DOMAIN, SEGMENTS, etc.)
			};

			console.log("📤 LLM API Payload:", JSON.stringify(payload, null, 2));

			const response = await this.makeAuthenticatedRequest(this.llmApiUrl, {
				method: "POST",
				body: JSON.stringify(payload),
			});

			if (!response.ok) {
				const errorText = await response.text();
				console.error("❌ LLM API Error Response:", {
					status: response.status,
					statusText: response.statusText,
					body: errorText,
				});
				throw new Error(`LLM API error: ${response.status} - ${errorText}`);
			}

			const data = await response.json();
			console.log("✅ LLM API response received:", data);

			return {
				success: true,
				data: data,
				error: null,
			};
		} catch (error) {
			console.error("❌ LLM API error:", error);
			return {
				success: false,
				data: null,
				error: error.message,
			};
		}
	}

	// ==================== DOMAIN SEARCH API METHODS ====================

	// ==================== ENHANCED SEARCH FLOW ====================

	async performCompleteSearch(searchQuery, searchType) {
		try {
			console.log("🚀 Starting complete search flow...");

			// Step 1: Call LLM API
			const llmResult = await this.searchWithLLM(searchQuery, searchType);
			if (!llmResult.success) {
				throw new Error("LLM search failed: " + llmResult.error);
			}

			// Step 2: Send LLM response DIRECTLY to Domain API
			const domainResult = await this.performDomainSearch(llmResult.data);
			if (!domainResult.success) {
				throw new Error("Domain search failed: " + domainResult.error);
			}

			return {
				success: true,
				llmResponse: llmResult.data,
				domainResponse: domainResult.data,
				error: null,
			};
		} catch (error) {
			console.error("❌ Complete search flow failed:", error);
			return {
				success: false,
				llmResponse: null,
				domainResponse: null,
				error: error.message,
			};
		}
	}

	// ==================== UTILITY METHODS ====================

	generateCSRFToken() {
		try {
			const array = new Uint8Array(16);
			crypto.getRandomValues(array);
			this.tokens.csrfToken = Array.from(array, (byte) =>
				byte.toString(16).padStart(2, "0"),
			).join("");
		} catch (error) {
			this.tokens.csrfToken = Math.random().toString(36).substr(2, 16);
		}
	}

	getTokenStatus() {
		return {
			hasToken: !!this.tokens.idToken,
			isValid: this.isIdTokenValid(),
			user: this.getUser()?.email,
			inStorage: !!localStorage.getItem(this.TOKEN_STORAGE_KEY),
		};
	}

	async testConnection() {
		try {
			const response = await this.makeAuthenticatedRequest(
				this.llmApiUrl.replace("/search-text", "/health"),
			);
			if (response.ok) {
				return { success: true, message: "API connection successful" };
			} else {
				return { success: false, error: `HTTP ${response.status}` };
			}
		} catch (error) {
			return { success: false, error: error.message };
		}
	}

	// ✅ ADD: Debug method to test LLM API manually
	async testLLMAPI(testQuery = "wellness mobile content") {
		console.log("🧪 Testing LLM API with query:", testQuery);

		const result = await this.searchWithLLM(testQuery, "domain");

		if (result.success) {
			console.log("✅ LLM API test successful!");
			console.log("Response data:", result.data);
		} else {
			console.error("❌ LLM API test failed:", result.error);
		}

		return result;
	}
}

// Export singleton instance
const llmApiService = new LLMApiService();
export default llmApiService;
